import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import ChecklistIcon from '@mui/icons-material/Checklist'; // Steps
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined'; // Motivations
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'; // Outcome
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Context
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'; // Enablers
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'; // Friction
import TaskAltIcon from '@mui/icons-material/TaskAlt'; // Task Label
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const PersonaTaskAnalysisTranscript = ({ summaryData }) => {
  const [expandedTask, setExpandedTask] = useState({});

  const handleAccordionChange = (personaIndex, taskIndex) => (event, isExpanded) => {
    const panelId = `task-panel-${personaIndex}-${taskIndex}`;
    setExpandedTask((prev) => ({
      ...prev,
      [panelId]: isExpanded,
    }));
  };

  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Persona Task analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Persona Task Analysis
          <AnalysisLensHelperText lensName="persona_task" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Overall Summary
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {results.summary || 'No summary provided.'}
          </Typography>
        </Box>

        {/* Personas Section */}
        {results.personas?.length > 0 ? (
          results.personas.map((persona, pIndex) => (
            <Paper
              key={pIndex}
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                p: { xs: 1.5, md: 2 },
                mb: 3,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PersonOutlineIcon /> {persona.persona_label}
              </Typography>

              {/* Context */}
              <Box sx={{ mb: 2, pl: 1 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
                >
                  <InfoOutlinedIcon fontSize="inherit" /> Context
                </Typography>
                <Typography variant="body2" sx={{ pl: 3 }}>
                  {persona.context}
                </Typography>
              </Box>

              {/* Motivations */}
              {persona.motivations?.length > 0 && (
                <Box sx={{ mb: 2, pl: 1 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    <EmojiObjectsOutlinedIcon fontSize="inherit" /> Motivations
                  </Typography>
                  <List dense disablePadding sx={{ pl: 3 }}>
                    {persona.motivations.map((motive, idx) => (
                      <ListItem key={idx} disableGutters sx={{ pb: 0.5 }}>
                        <ListItemText
                          primary={motive}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Tasks Accordions */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Tasks Attempted by {persona.persona_label}
              </Typography>

              {persona.tasks_attempted?.length > 0 ? (
                persona.tasks_attempted.map((task, tIndex) => {
                  const panelId = `task-panel-${pIndex}-${tIndex}`;
                  const isExpanded = expandedTask[panelId] || false;

                  return (
                    <Accordion
                      key={panelId}
                      expanded={isExpanded}
                      onChange={handleAccordionChange(pIndex, tIndex)}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1.5,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: '0 0 12px 0' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${panelId}-content`}
                        id={`${panelId}-header`}
                        sx={{
                          bgcolor: isExpanded ? 'grey.50' : 'background.paper',
                          borderBottom: isExpanded ? 1 : 0,
                          borderColor: 'divider',
                          borderTopLeftRadius: 'inherit',
                          borderTopRightRadius: 'inherit',
                          minHeight: 64,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <TaskAltIcon color="primary" />
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 'medium', flexGrow: 1 }}
                          >
                            {task.task_label}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                        <Grid container spacing={3}>
                          {/* Steps */}
                          {task.steps?.length > 0 && (
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <ChecklistIcon fontSize="inherit" color="action" /> Steps
                              </Typography>
                              <List dense disablePadding sx={{ pl: 1 }}>
                                {task.steps.map((step, idx) => (
                                  <ListItem key={idx} disableGutters sx={{ pb: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: '20px', mr: 1 }}>
                                      <Typography variant="body2">{idx + 1}.</Typography>
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={step}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Grid>
                          )}

                          {/* Friction Points */}
                          {task.friction_points?.length > 0 && (
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <ReportProblemOutlinedIcon fontSize="inherit" color="warning" />{' '}
                                Friction Points
                              </Typography>
                              <List dense disablePadding sx={{ pl: 1 }}>
                                {task.friction_points.map((point, idx) => (
                                  <ListItem key={idx} disableGutters sx={{ pb: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: '20px', mr: 1 }}>
                                      <ReportProblemOutlinedIcon
                                        fontSize="inherit"
                                        color="warning"
                                        sx={{ fontSize: '1rem' }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={point}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Grid>
                          )}

                          {/* Enablers */}
                          {task.enablers?.length > 0 && (
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <LightbulbOutlinedIcon fontSize="inherit" color="success" />{' '}
                                Enablers
                              </Typography>
                              <List dense disablePadding sx={{ pl: 1 }}>
                                {task.enablers.map((enabler, idx) => (
                                  <ListItem key={idx} disableGutters sx={{ pb: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: '20px', mr: 1 }}>
                                      <LightbulbOutlinedIcon
                                        fontSize="inherit"
                                        color="success"
                                        sx={{ fontSize: '1rem' }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={enabler}
                                      primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Grid>
                          )}

                          {/* Outcome */}
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              <FlagOutlinedIcon fontSize="inherit" color="primary" /> Outcome
                            </Typography>
                            <Typography variant="body2" sx={{ pl: 3 }}>
                              {task.outcome || 'N/A'}
                            </Typography>
                          </Grid>

                          {/* Quotes */}
                          {task.quotes?.length > 0 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <FormatQuoteIcon fontSize="inherit" color="action" /> Supporting
                                Quotes
                              </Typography>
                              <List dense disablePadding sx={{ pl: 2 }}>
                                {task.quotes.map((quote, idx) => (
                                  <ListItem
                                    key={idx}
                                    disableGutters
                                    sx={{
                                      alignItems: 'flex-start',
                                      '&:not(:last-child)': { mb: 1 },
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                      <FormatQuoteIcon
                                        fontSize="small"
                                        sx={{ transform: 'scaleX(-1)' }}
                                        color="disabled"
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={quote}
                                      primaryTypographyProps={{
                                        variant: 'body2',
                                        fontStyle: 'italic',
                                      }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Grid>
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', mt: 2 }}
                >
                  No specific tasks identified for this persona.
                </Typography>
              )}
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No personas identified in this analysis.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PersonaTaskAnalysisTranscript;
